<template>
  <div />
</template>

<script setup lang="ts">
import { watch, ref, computed, onMounted, onBeforeUnmount } from 'vue'
import type { Ref, ComputedRef } from 'vue'
import type {
  AudioAnalyserVolumeCallbackData,
  AudioAnalyserRecordingCallbackData,
  TranscriptionResult,
  WordOrPhraseMatch,
  ManagedTimeout,
  progressSateError
} from '@/customTypes'
import { storeToRefs } from 'pinia'

// Store
import { useDataStore } from '@/stores/DataStore'
import { useMediaDevicesStore } from '@/stores/MediaDevicesStore'
import { useSoundEffectsStore } from '@/stores/SoundEffectsStore'
import { useLiveSpeechStore } from '@/stores/LiveSpeechStore'
import { useAudioAnalyserStore } from '@/stores/AudioAnalyserStore'
import { useActiveScreenerStore } from '@/stores/activeScreenerStore'

// Helpers
import { useTimeout } from '@/utils/helpers'

import Logger from '@/services/Logger.js'

const logger: any = new Logger('RecordingComponent', true)

const COMPONENT_ERROR_TYPE: progressSateError = 'NoAnswersDetected'

const dataStore = useDataStore()
const { currentQuestion, currentSubtest } = storeToRefs(dataStore)

const mediaDevicesStore = useMediaDevicesStore()
const soundEffectsStore = useSoundEffectsStore()
const { audioContext } = storeToRefs(soundEffectsStore)

const liveSpeechStore = useLiveSpeechStore()
const audioAnalyserStore = useAudioAnalyserStore()
const activeScreenerStore = useActiveScreenerStore()
const { setManagedTimeout, clearManagedTimeout } = useTimeout()

const props = defineProps({
  questionId: { type: Number, default: 0 },
  waitTime: { type: Number, default: 0 },
  record: { type: Boolean, default: false },
  waitingForAnswer: { type: Boolean, default: false },
  isPracticeQuestion: { type: Boolean, default: false },
  proceedButton: { type: Function, required: true }
})

const emit = defineEmits<{
  (e: 'recording-finished', answered: boolean): void
  (e: 'play-prompt'): void
}>()

const attemptCount: Ref<number> = ref(1)

const threshold: number = 7
const soundRecorded: Ref<boolean> = ref(false)

const wordsDetectedInAudio: Ref<boolean> = ref(false)
let speechTimeout = ref<ManagedTimeout | null>(null)
let waitingRecordingTimeout = ref<ManagedTimeout | null>(null)
const liveTranscribing: Ref<boolean> = ref(false)
let earlyStopTriggered = false
let waitTimeElapsed = false

const analyserSetup: Ref<boolean> = ref(false)
const noTranscriptionCount: Ref<number> = ref(0)
const hadEmptyRecording = ref(false)
const deepgramFallbackActive: Ref<boolean> = ref(false)
const deepgramWarningSent: Ref<boolean> = ref(false)
const deepgramTranscriptionLog: Ref<string> = ref('')
const transcriptionSegmentOpen: Ref<boolean> = ref(false)
const soundRecordedSamples: Ref<number[]> = ref([])
const MAX_SOUND_RECORDED_SAMPLES = 25

const recordingStreamReady: ComputedRef<boolean> = computed(() => {
  return !!(mediaDevicesStore.recordingStream && soundEffectsStore.audioContext)
})

const wordOrAudioDetected = computed(() => {
  return liveTranscribing.value ? wordsDetectedInAudio.value : soundRecorded.value
})

const screenerHasComponentError = computed(() =>
  activeScreenerStore.hasErrorState(COMPONENT_ERROR_TYPE)
)

onMounted(() => {
  const stopWatching = watch(recordingStreamReady, (stream) => {
    if (stream) {
      logger.info('Recording Stream Ready setup analyser')
      analyserSetup.value = true
      setupAudioAnalyser()
      stopWatching()
    }
  })

  if (recordingStreamReady.value) {
    logger.info('Recording Stream already Ready setup analyser')
    stopWatching()
    if (!analyserSetup.value) {
      analyserSetup.value = true
      setupAudioAnalyser()
    }
  }
})

onBeforeUnmount(() => {
  stopRecording()
  audioAnalyserStore.destroyAudioAnalyser()
})

watch(
  () => props.waitingForAnswer,
  (newVal) => {
    if (newVal === false || !props.record) return
    if (!recordingStreamReady.value) {
      logger.error(
        'Failed to wait for answer. No Stream exists:',
        mediaDevicesStore.recordingStream
      )
    }
    soundRecorded.value = false
    wordsDetectedInAudio.value = false
    waitForAudioAnswer()
  }
)

function initiateRecording(): void {
  startRecording()
}

const formatErrorForLog = (error: unknown) => {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack
    }
  }

  if (typeof error === 'string') {
    return { message: error }
  }

  if (error && typeof error === 'object') {
    try {
      return JSON.parse(JSON.stringify(error))
    } catch {
      return { message: String(error) }
    }
  }

  return { message: String(error ?? 'Unknown error') }
}

const logDeepgramEvent = async (event: string, details: Record<string, any> = {}) => {
  try {
    await activeScreenerStore.logToDatabase(event, {
      questionId: props.questionId,
      subtestKey: currentSubtest.value?.key ?? null,
      fallbackActive: deepgramFallbackActive.value,
      ...details
    })
  } catch (error) {
    logger.error('Failed to log Deepgram event', { event, error })
  }
}

const activateDeepgramFallback = (reason: string, extra: Record<string, any> = {}) => {
  if (deepgramFallbackActive.value) return
  deepgramFallbackActive.value = true
  liveTranscribing.value = false
  logger.warn('Activating Deepgram fallback', { reason, extra })
  void logDeepgramEvent('deepgram_fallback_activated', { reason, ...extra })
}

const handleDeepgramRuntimeError = (error: unknown) => {
  logger.error('Deepgram runtime error', error)
  const errorDetails = formatErrorForLog(error)
  void logDeepgramEvent('deepgram_error', { error: errorDetails })
  activateDeepgramFallback('runtime_error', { error: errorDetails })
  liveSpeechStore.stopTranscription()
  void notifyTeacherOfDeepgramIssue('runtime_error', errorDetails)
}

const handleDeepgramSetupFailure = (error: unknown) => {
  logger.error('Failed to start Deepgram transcription', error)
  const errorDetails = formatErrorForLog(error)
  liveTranscribing.value = false
  void logDeepgramEvent('deepgram_setup_failed', { error: errorDetails })
  activateDeepgramFallback('setup_failed', { error: errorDetails })
  void notifyTeacherOfDeepgramIssue('setup_failed', errorDetails)
}

const appendToTranscriptionLog = (value: string) => {
  deepgramTranscriptionLog.value = `${deepgramTranscriptionLog.value}${value}`
}

const openTranscriptionSegment = () => {
  if (transcriptionSegmentOpen.value) return
  appendToTranscriptionLog('(')
  soundRecordedSamples.value = []
  transcriptionSegmentOpen.value = true
}

const closeTranscriptionSegment = () => {
  if (!transcriptionSegmentOpen.value) return
  appendToTranscriptionLog('),')
  transcriptionSegmentOpen.value = false
  appendSoundRecordedSegment()
}

const appendTranscriptionText = (text: string) => {
  const cleaned = text?.trim()
  if (!cleaned) return
  if (!transcriptionSegmentOpen.value) {
    openTranscriptionSegment()
  }
  appendToTranscriptionLog(`${cleaned} `)
}

const appendSoundRecordedSegment = () => {
  const samples = soundRecordedSamples.value
  const payload = samples.length > 0 ? samples.map((sample) => sample.toFixed(1)).join(',') : 'none'
  appendToTranscriptionLog(`(Sound levels: ${payload}),`)
  soundRecordedSamples.value = []
}

const recordSoundSample = (value: number) => {
  const rounded = Math.round(value * 10) / 10
  soundRecordedSamples.value.push(rounded)
  if (soundRecordedSamples.value.length > MAX_SOUND_RECORDED_SAMPLES) {
    soundRecordedSamples.value.shift()
  }
}

const notifyTeacherOfDeepgramIssue = async (
  reason: string,
  errorDetails: Record<string, any> | null
) => {
  if (deepgramWarningSent.value) return
  deepgramWarningSent.value = true
  await activeScreenerStore.sendScreenerWarning({
    type: 'DeepgramUnavailable',
    message:
      'Live transcription is unavailable. This can lead to more pausing issues and failure to acknowledge a pupils responses. Please look in the screener guide to make sure the firewall is setup for Deepgram.',
    priority: 1,
    metadata: {
      reason,
      errorDetails
    }
  })
}

const warnOnNonPreferredMicrophoneUse = async () => {
  if (!mediaDevicesStore.hasPreferredInputDeviceAvailable()) return
  if (mediaDevicesStore.isPreferredInputDeviceSelected()) return

  const selectedDevice = mediaDevicesStore.getCurrentDeviceInfo('input')
  const preferredDevices = mediaDevicesStore.getPreferredInputDeviceSummaries()

  await activeScreenerStore.sendScreenerWarning({
    type: 'IncorrectMicInUse',
    message:
      'The recording isn’t using the available recommended headset, this will affect audio quality and may lead to inconclusive reports. Please exit and re-enter the tech test',
    priority: 1,
    metadata: {
      selectedDevice,
      preferredDevices
    }
  })
}

const resolveIncorrectMicInUseWarning = async () => {
  if (
    activeScreenerStore.hasWarningState('IncorrectMicInUse') &&
    mediaDevicesStore.isPreferredInputDeviceSelected()
  ) {
    await activeScreenerStore.sendScreenerResolvedWarning({
      type: 'IncorrectMicInUse'
    })
  }
}

const resolveDeepgramUnavailableWarning = async () => {
  if (activeScreenerStore.hasWarningState('DeepgramUnavailable')) {
    await activeScreenerStore.sendScreenerResolvedWarning({
      type: 'DeepgramUnavailable'
    })
  }
}

function waitForAudioAnswer(): void {
  earlyStopTriggered = false
  waitTimeElapsed = false
  deepgramFallbackActive.value = false
  deepgramTranscriptionLog.value = ''
  transcriptionSegmentOpen.value = false

  if (!audioAnalyserStore.isRecording) {
    startRecording()
  } else {
    openTranscriptionSegment()
  }
  if (currentQuestion.value) {
    const deepgramTargets: WordOrPhraseMatch | undefined = liveSpeechStore.convertTargetFormat(
      currentQuestion.value,
      currentSubtest.value?.key
    )
    if (deepgramTargets) {
      liveSpeechStore.setItemsToMatch(deepgramTargets)
    }
  }

  logger.info('Waiting for audio answer', props.waitTime)
  liveTranscribing.value = true
  liveSpeechStore
    .startTranscription((result: TranscriptionResult) => {
      if (result.text) {
        if (noTranscriptionCount.value >= 1 || screenerHasComponentError.value) {
          sendResponsiveNotification()
        }
        logger.info('Transcription result', result.text)
        appendTranscriptionText(result.text)
        noTranscriptionCount.value = 0
        wordsDetectedInAudio.value = true
        resolveDeepgramUnavailableWarning()
        if (waitTimeElapsed) return
        if (result.matched && !earlyStopTriggered) {
          skipWaitingForAnswer()
        }
        clearManagedTimeout(speechTimeout)

        speechTimeout.value = setManagedTimeout(() => {
          if (!earlyStopTriggered && currentSubtest.value?.metadata?.buttonProgress) {
            props.proceedButton()
          }
        }, 1000)
      }
    }, handleDeepgramRuntimeError)
    .catch((error: unknown) => {
      handleDeepgramSetupFailure(error)
    })

  waitingRecordingTimeout.value = setManagedTimeout(() => {
    if (earlyStopTriggered) return
    waitTimeElapsed = true
    logger.info('wait time elapsed')
    clearManagedTimeout(speechTimeout)
    stopRecording(checkForAnswer)
  }, props.waitTime * 1000)
}

function skipWaitingForAnswer() {
  logger.info('Wait time Skipped')
  earlyStopTriggered = true
  clearManagedTimeout(waitingRecordingTimeout)
  clearManagedTimeout(speechTimeout)
  stopRecording(checkForAnswer)
}

function startRecording() {
  logger.info('Starting recording')
  openTranscriptionSegment()
  if (!recordingStreamReady.value) {
    logger.error('No recording stream unable to start Recording')
    dataStore.setScreenerError(
      'recordingError',
      'Sorry, there was an error recording the users answer. Please refresh the page and try again. \n If the problem persists, please contact the support team.'
    )
  }
  liveTranscribing.value = false
  audioAnalyserStore.startRecordingSound()
}
/**
 * Check if audio analyzer recorded any sound and if so, emit the recording-finished, else for practice questions, play the prompt.
 */
async function checkForAnswer({ inputBlob, inputDuration }: AudioAnalyserRecordingCallbackData) {
  if (await checkForEmptyRecording(inputBlob)) return

  await activeScreenerStore.logToDatabase('recording_summary', {
    questionId: props.questionId,
    subtestKey: currentSubtest.value?.key ?? null,
    transcriptionLog: deepgramTranscriptionLog.value,
    deepgramFallbackActive: deepgramFallbackActive.value,
    wordOrAudioDetected: wordOrAudioDetected.value,
    inputDuration: inputDuration,
    inputBlobSize: inputBlob.size
  })

  if (!wordOrAudioDetected.value) {
    noTranscriptionCount.value++
    // Skip forced pause for nonword subtest since nonwords often fail transcription
    //const isNonwordSubtest = currentSubtest.value?.key === 'nonword' ... && !isNonwordSubtest
    if (noTranscriptionCount.value >= 3) {
      const transcriptionLog = deepgramTranscriptionLog.value || 'No transcription detected'


      await activeScreenerStore.logToDatabase('no_answers_detected', {
        questionId: props.questionId,
        subtestKey: currentSubtest.value?.key ?? null,
        message: 'No answers detected for the last few questions.',
        transcriptionLog
      })

      const enforceNoAnswersPause = _ENFORCE_NO_ANSWERS_PAUSE_?.toLowerCase() === 'true'

      if (enforceNoAnswersPause) {
        await activeScreenerStore.sendScreenerError({
          type: COMPONENT_ERROR_TYPE,
          message:
            'No answers detected for the last few questions, lack of answers will result in an inconclusive report.',
          priority: 2,
          metadata: {
            transcriptionLog
          }
        })

        dataStore.setPausedState(true)
        return
      }
    }
  } else {
    noTranscriptionCount.value = 0
  }

  if (wordOrAudioDetected.value || !props.isPracticeQuestion || attemptCount.value > 1) {
    logger.info('Answer given')
    emit('recording-finished', wordOrAudioDetected.value)
    dataStore.audioAnswerGiven(inputBlob, wordOrAudioDetected.value, inputDuration)
    await resolveIncorrectMicInUseWarning()
    await warnOnNonPreferredMicrophoneUse()
    reset()
  } else {
    emit('play-prompt')
    attemptCount.value++
  }
}

async function checkForEmptyRecording(inputBlob: Blob): Promise<boolean> {
  if (inputBlob.size === 0) {
    if (hadEmptyRecording.value) {
      dataStore.setScreenerError(
        'AudioRecordingError',
        'The recorded audio is empty. Please try recording again.'
      )

      return true
    }
    hadEmptyRecording.value = true

    // Attempt recovery of recording stream and play a prompt for the question
    await mediaDevicesStore.closeRecordingStream()
    mediaDevicesStore.setAudioRecordingStream().then(() => {
      setupAudioAnalyser()
      // Play random prompt for the question to repeat recording attempt
      emit('play-prompt')
    })
    return true
  }
  hadEmptyRecording.value = false
  return false
}

/**
 * Sets up the audio analysis for the recording stream.
 */
function setupAudioAnalyser() {
  logger.info('Setting up audio analyser')
  audioAnalyserStore.setupAudioAnalyser(
    audioContext.value as AudioContext,
    'audioinput',
    mediaDevicesStore.recordingStream as MediaStream,
    volumeChangeCallback
  )
}

function volumeChangeCallback({ average }: AudioAnalyserVolumeCallbackData) {
  if (average > threshold) {
    soundRecorded.value = true
    recordSoundSample(average)
  }
}

/**
 * Reset all of the recording properties to their initial values.
 * @returns {void}
 */
function reset() {
  soundRecorded.value = false
  attemptCount.value = 1
  deepgramFallbackActive.value = false
}

function stopRecording(callback?: Function) {
  logger.info('Stopping recording')
  closeTranscriptionSegment()
  liveSpeechStore.stopTranscription()
  audioAnalyserStore.stopRecordingSound(callback)
}

function sendResponsiveNotification() {
  activeScreenerStore.sendScreenerResolvedError({
    type: COMPONENT_ERROR_TYPE
  })
}

defineExpose({
  skipWaitingForAnswer,
  initiateRecording
})
</script>

<style lang="scss"></style>
